import { z } from 'zod';
import { PaymentCurrencySchema } from './payment';
/**
 * Referral credit contract (Phase 14 — B2B portal).
 *
 * When a user who signed up under someone's `referralCode` closes their FIRST
 * deal, the referrer earns a one-off `referral_credit`. Credits are recorded in
 * a DEDICATED append-only `referral_credits` collection — deliberately NOT the
 * payments ledger — so no `biddingPower`/money value moves while the custody
 * model is open (a future, payments-gated step converts credits to real value).
 *
 * IDEMPOTENCY: exactly one credit per referred user's first closed deal. The
 * `referredUserId` is the idempotency key (a UNIQUE index enforces it), so a
 * replayed or double-fired "deal closed" event can never double-credit.
 */
export const ReferralCreditSchema = z.object({
    _id: z.unknown().nullish(),
    /** The invited user whose first closed deal triggered the credit (idempotency key). */
    referredUserId: z.string(),
    /** The referrer who earns the credit. */
    referrerUserId: z.string(),
    /** The referral code that linked them (audit). */
    referralCode: z.string().nullish(),
    /** The deal whose closing triggered the credit (audit). */
    dealId: z.string().nullish(),
    amount: z.number().nonnegative(),
    currency: PaymentCurrencySchema.default('EUR'),
    createdAt: z.coerce.date(),
});
/** Default one-off referral reward magnitude (EUR). Data, not money movement. */
export const DEFAULT_REFERRAL_CREDIT_AMOUNT = 50;
/** Referral codes are compared case-insensitively; normalize before store/lookup. */
export function normalizeReferralCode(code) {
    return code.trim().toUpperCase();
}
/** True for a syntactically valid referral code (6–12 uppercase alphanumerics). */
export const REFERRAL_CODE_REGEX = /^[A-Z0-9]{6,12}$/;
export function isValidReferralCode(code) {
    return REFERRAL_CODE_REGEX.test(normalizeReferralCode(code));
}
//# sourceMappingURL=referral.js.map